<script setup lang="ts">
import { ref } from "vue";

import { IActivateProgramCommand } from "@/composables/kili/internal/mutations/use-activate-site-program-mutation";
import { IDeleteProgramCommand } from "@/composables/kili/internal/mutations/use-delete-site-program-mutation";
import { ProgramId, V4HSiteId } from "@/data/common/ids";
import { SiteProgramOverview } from "@/domains/v4b/program/program.types";
import { useTypedIntl } from "@/locales/typed-intl";
import { cn } from "@/utils/libs/tailwind-utils";

import ToggleSwitch from "primevue/toggleswitch";

import HospitalityButton from "@/components/ui/buttons/hospitality-button.vue";
import ConfirmationDialog from "@/components/ui/dialog/confirmation-dialog.vue";
import DropMenu, { IDropMenuItem } from "@/components/ui/menu/drop-menu.vue";

interface IProps {
  program: SiteProgramOverview;
  siteId: V4HSiteId;
  isActive: boolean;
  disabled?: boolean;
}

interface IEmits {
  (e: "toggle", params: IActivateProgramCommand): void;
  (e: "select", programId: ProgramId): void;
  (e: "delete", command: IDeleteProgramCommand): void;
  (e: "rename", program: SiteProgramOverview): void;
  (e: "duplicate", program: SiteProgramOverview): void;
}

const props = withDefaults(defineProps<IProps>(), {
  disabled: false,
});
const emits = defineEmits<IEmits>();

const { t, k } = useTypedIntl();

const isDeleteProgramConfirmationDialogVisible = ref<boolean>(false);

const dropMenuItems = ref<IDropMenuItem[]>([
  {
    label: "global.misc.rename",
    icon: "pi pi-pencil",
    callback: () => {
      if (props.disabled) return;
      emits("rename", props.program);
    },
  },
  {
    label: "control.programming.copyToOtherSite.title",
    icon: "pi pi-copy",
    callback: () => {
      if (props.disabled) return;
      emits("duplicate", props.program);
    },
  },
  {
    label: "global.misc.delete",
    icon: "pi pi-trash",
    callback: () => {
      if (props.disabled) return;
      isDeleteProgramConfirmationDialogVisible.value = true;
    },
  },
]);

function toggleModel(nextIsActive: boolean) {
  if (props.disabled) return;

  emits("toggle", {
    programId: props.program.id,
    siteId: props.siteId,
    isActive: nextIsActive,
    name: props.program.name,
  });
}

function handleConfirmDeletion() {
  if (props.disabled) return;

  isDeleteProgramConfirmationDialogVisible.value = false;
  emits("delete", { programId: props.program.id, siteId: props.siteId });
}

function selectProgram() {
  if (props.disabled) return;

  emits("select", props.program.id);
}
</script>

<template>
  <div
    class="bg-white rounded-md p-4 flex gap-4"
    data-test-id="program-overview-card"
  >
    <div
      :aria-label="props.program.name"
      :aria-disabled="props.disabled"
      :class="
        cn('flex flex-grow items-center justify-between', props.disabled ? 'cursor-not-allowed' : 'cursor-pointer')
      "
      role="button"
      :tabindex="props.disabled ? -1 : 0"
      @click="selectProgram"
      @keydown.enter="selectProgram"
    >
      <div class="flex flex-col">
        <h4>
          <template v-if="program.isSiteDefault">
            {{ t(k.control.programming.defaultProgram) }}
          </template>
          <template v-else>{{ props.program.name }}</template>
        </h4>
      </div>
    </div>

    <div class="flex flex-col gap-4">
      <DropMenu
        :items="dropMenuItems"
        right
      >
        <HospitalityButton
          data-test-id="program-options-button"
          :disabled="props.disabled"
          is-icon-only
          severity="tertiary"
          size="xs"
        >
          <i class="pi pi-ellipsis-v" />
        </HospitalityButton>
      </DropMenu>

      <ToggleSwitch
        :disabled="props.disabled"
        :model-value="props.isActive"
        class="shrink-0"
        data-test-id="toggle-program-switch"
        @update:model-value="toggleModel"
      />
    </div>
  </div>

  <ConfirmationDialog
    v-model="isDeleteProgramConfirmationDialogVisible"
    :callback="() => handleConfirmDeletion()"
    delete-mode
    :subtitle="t(k.control.programming.deletion.subtitle({ name: props.program.name }))"
    :title="t(k.control.programming.deletion.title)"
  />
</template>
